import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Transaction } from '@/context/FinanceContext';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ExportData {
  transactions: Transaction[];
  categories: { id: string; name: string }[];
  accounts: { id: string; name: string }[];
  companies: { id: string; name: string }[];
  paymentMethods: { id: string; name: string }[];
  costCenters: { id: string; name: string }[];
  suppliers: { id: string; name: string }[];
}

interface ColumnConfig {
  key: string;
  title: string;
  width: number;
  visible: boolean;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "type", title: "Tipo", width: 80, visible: true },
  { key: "issueDate", title: "Emissão", width: 120, visible: true },
  { key: "dueDate", title: "Vencimento", width: 120, visible: true },
  { key: "paymentDate", title: "Pagamento", width: 120, visible: true },
  { key: "description", title: "Descrição", width: 180, visible: true },
  { key: "value", title: "Valor", width: 100, visible: true },
  { key: "parcels", title: "Parcelas", width: 80, visible: true },
  { key: "supplier", title: "Fornecedor", width: 120, visible: true },
  { key: "category", title: "Plano de Contas", width: 130, visible: true },
  { key: "account", title: "Conta Financeira", width: 110, visible: true },
  { key: "paymentMethod", title: "Forma de Pagamento", width: 100, visible: true },
  { key: "status", title: "Status", width: 80, visible: true },
  { key: "costCenter", title: "Centro Custo", width: 120, visible: true },
];

export const useExport = () => {
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatValueForExport = (value: number, type: 'receita' | 'despesa') => {
    // Receitas: valor positivo
    // Despesas: valor negativo
    return type === 'receita' ? Math.abs(value) : -Math.abs(value);
  };

  const getColumnValue = (
    transaction: Transaction,
    columnKey: string,
    categoryMap: Map<string, string>,
    accountMap: Map<string, string>,
    supplierMap: Map<string, string>,
    paymentMethodMap: Map<string, string>,
    costCenterMap: Map<string, string>,
    formatValue: (value: number, type: 'receita' | 'despesa') => any,
    useCurrency: boolean = false
  ): any => {
    switch (columnKey) {
      case 'type':
        return transaction.type === 'receita' ? 'Receita' : 'Despesa';
      case 'issueDate':
        return formatDate(transaction.issueDate);
      case 'dueDate':
        return formatDate(transaction.dueDate);
      case 'paymentDate':
        return formatDate(transaction.paymentDate);
      case 'description':
        return transaction.description;
      case 'value':
        return useCurrency 
          ? formatCurrency(transaction.value) 
          : formatValue(transaction.value, transaction.type);
      case 'parcels':
        return transaction.parcels || '';
      case 'supplier':
        return supplierMap.get(transaction.supplierId || '') || '';
      case 'category':
        return categoryMap.get(transaction.categoryId) || 'N/A';
      case 'account':
        return accountMap.get(transaction.accountId) || 'N/A';
      case 'paymentMethod':
        return paymentMethodMap.get(transaction.paymentMethodId || '') || '';
      case 'status':
        return transaction.settled !== false ? 'Pago' : 'Em Aberto';
      case 'costCenter':
        return costCenterMap.get(transaction.costCenterId || '') || '';
      default:
        return '';
    }
  };

  const prepareExportDataForExcel = (data: ExportData, columnConfig?: ColumnConfig[]) => {
    const categoryMap = new Map(data.categories.map(c => [c.id, c.name]));
    const accountMap = new Map(data.accounts.map(a => [a.id, a.name]));
    const paymentMethodMap = new Map(data.paymentMethods.map(p => [p.id, p.name]));
    const costCenterMap = new Map(data.costCenters.map(c => [c.id, c.name]));
    
    // Use suppliers passed via parameter
    const supplierMap = new Map(data.suppliers.map(s => [s.id, s.name]));

    // Use provided column config or default visible columns
    const columns = columnConfig?.filter(col => col.visible) || DEFAULT_COLUMNS.filter(col => col.visible);

    return data.transactions.map(transaction => {
      const row: Record<string, any> = {};
      
      columns.forEach(column => {
        row[column.title] = getColumnValue(
          transaction,
          column.key,
          categoryMap,
          accountMap,
          supplierMap,
          paymentMethodMap,
          costCenterMap,
          formatValueForExport,
          false // Excel uses numeric values
        );
      });
      
      return row;
    });
  };

  const prepareExportDataForPDF = (data: ExportData, columnConfig?: ColumnConfig[]) => {
    const categoryMap = new Map(data.categories.map(c => [c.id, c.name]));
    const accountMap = new Map(data.accounts.map(a => [a.id, a.name]));
    const paymentMethodMap = new Map(data.paymentMethods.map(p => [p.id, p.name]));
    const costCenterMap = new Map(data.costCenters.map(c => [c.id, c.name]));
    
    // Use suppliers passed via parameter
    const supplierMap = new Map(data.suppliers.map(s => [s.id, s.name]));

    // Use provided column config or default visible columns
    const columns = columnConfig?.filter(col => col.visible) || DEFAULT_COLUMNS.filter(col => col.visible);

    return data.transactions.map(transaction => {
      const row: Record<string, any> = {};
      
      columns.forEach(column => {
        row[column.title] = getColumnValue(
          transaction,
          column.key,
          categoryMap,
          accountMap,
          supplierMap,
          paymentMethodMap,
          costCenterMap,
          formatValueForExport,
          true // PDF uses currency formatting
        );
      });
      
      return row;
    });
  };

  const exportToExcel = async (data: ExportData, filename: string = 'lancamentos', columnConfig?: ColumnConfig[]) => {
    try {
      const exportData = prepareExportDataForExcel(data, columnConfig);
      
      if (exportData.length === 0) {
        throw new Error('Nenhum dado para exportar');
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Lançamentos');

      // Get column headers from the first row
      const headers = Object.keys(exportData[0]);
      
      // Add header row
      worksheet.addRow(headers);
      
      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF475569' }
      };
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };

      // Add data rows
      exportData.forEach(row => {
        worksheet.addRow(Object.values(row));
      });

      // Auto-fit column widths
      worksheet.columns.forEach((column, index) => {
        const header = headers[index];
        let maxLength = header.length;
        
        exportData.forEach(row => {
          const value = String(row[header] || '');
          if (value.length > maxLength) {
            maxLength = value.length;
          }
        });
        
        column.width = Math.min(maxLength + 2, 50);
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      
      const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${timestamp}.xlsx`;
      link.click();
      
      URL.revokeObjectURL(url);
      
      return { success: true, message: 'Arquivo Excel exportado com sucesso!' };
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      return { success: false, message: 'Erro ao exportar arquivo Excel.' };
    }
  };

  const exportToPDF = (data: ExportData, filename: string = 'lancamentos', columnConfig?: ColumnConfig[]) => {
    try {
      const exportData = prepareExportDataForPDF(data, columnConfig);
      
      if (exportData.length === 0) {
        throw new Error('Nenhum dado para exportar');
      }

      const doc = new jsPDF('landscape');
      
      // Título
      doc.setFontSize(16);
      doc.text('Relatório de Lançamentos', 14, 22);
      
      // Data de geração
      doc.setFontSize(10);
      doc.text(`Gerado em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}`, 14, 30);

      // Resumo
      const receitas = data.transactions.filter(t => t.type === 'receita').reduce((sum, t) => sum + t.value, 0);
      const despesas = data.transactions.filter(t => t.type === 'despesa').reduce((sum, t) => sum + t.value, 0);
      const saldo = receitas - despesas;

      doc.setFontSize(12);
      doc.text('Resumo:', 14, 45);
      doc.setFontSize(10);
      doc.text(`Total de Receitas: ${formatCurrency(receitas)}`, 14, 52);
      doc.text(`Total de Despesas: ${formatCurrency(despesas)}`, 14, 58);
      doc.text(`Saldo: ${formatCurrency(saldo)}`, 14, 64);

      // Tabela
      autoTable(doc, {
        head: [Object.keys(exportData[0])],
        body: exportData.map(row => Object.values(row)),
        startY: 75,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [71, 85, 105], fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        theme: 'striped'
      });

      const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
      doc.save(`${filename}_${timestamp}.pdf`);
      
      return { success: true, message: 'Arquivo PDF exportado com sucesso!' };
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      return { success: false, message: 'Erro ao exportar arquivo PDF.' };
    }
  };

  return {
    exportToExcel,
    exportToPDF
  };
};
